import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { AlertOctagon, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "../lib/cn";

export type ToastTone = "neutral" | "success" | "warning" | "danger" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /**
   * Milissegundos. Padrão: 4000, ou 5000 quando há ação ("desfazer" precisa
   * de tempo de leitura). `0` = nunca some sozinho — reservado a CRITICAL.
   */
  duration?: number;
  action?: ToastAction;
}

export interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  toasts: readonly ToastItem[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast() precisa de um <ToastProvider> acima na árvore.");
  return ctx;
}

export interface ToastProviderProps {
  children: ReactNode;
  /** Quantos ficam na tela ao mesmo tempo. O mais antigo cai fora. */
  max?: number;
}

/**
 * Toast headless, sem dependência externa: um provider, uma fila e uma
 * região `aria-live`. Nada de portal — a região é `fixed`, que basta.
 */
export function ToastProvider({ children, max = 3 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const duration = options.duration ?? (options.action ? 5000 : 4000);

      setToasts((current) => [...current, { ...options, id }].slice(-max));

      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss, max],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toast, dismiss, toasts }),
    [toast, dismiss, toasts],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster />
    </ToastContext.Provider>
  );
}

const TONE_ICON = {
  neutral: Info,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertOctagon,
} as const;

const TONE_TEXT: Record<ToastTone, string> = {
  neutral: "text-muted-foreground",
  info: "text-info",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

export interface ToastProps {
  tone?: ToastTone;
  title: ReactNode;
  description?: ReactNode;
  action?: ToastAction;
  /** Presente = mostra o X de dispensar. */
  onDismiss?: () => void;
  /** `alert` para o que não some sozinho (CRITICAL); `status` para o resto. */
  role?: "alert" | "status";
  className?: string;
}

/**
 * O cartão de um aviso, sem fila nem timer. O `Toaster` usa este mesmo
 * componente; exportado para a vitrine e para quem precisa do visual inline.
 */
export function Toast({
  tone = "neutral",
  title,
  description,
  action,
  onDismiss,
  role = "status",
  className,
}: ToastProps) {
  const Icon = TONE_ICON[tone];

  return (
    <div
      role={role}
      data-tone={tone}
      className={cn(
        "pointer-events-auto flex items-start gap-2.5 rounded-lg border border-border bg-surface-raised py-3 pl-3.5 pr-3 shadow-md",
        "animate-toast-in motion-reduce:animate-none",
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn("mt-px size-4 shrink-0", TONE_TEXT[tone])} />

      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description ? (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={() => {
              action.onClick();
              onDismiss?.();
            }}
            className="mt-1.5 rounded-xs text-xs font-medium text-primary-text underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {action.label}
          </button>
        ) : null}
      </div>

      {onDismiss ? (
        <button
          type="button"
          aria-label="Dispensar aviso"
          onClick={onDismiss}
          className="hit-44 -mr-1 -mt-1 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Região dos toasts: canto inferior-direito no desktop, topo no mobile
 * (abaixo da topbar, respeitando safe-area) — seção 6.
 */
export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <div
      aria-live="polite"
      aria-relevant="additions text"
      className={cn(
        "pointer-events-none fixed z-50 flex flex-col gap-2",
        // oxlint-disable-next-line eslint-js/no-restricted-syntax -- 0px é só o fallback de env(safe-area-inset-top), não medida de layout
        "inset-x-3 top-[calc(var(--spacing-topbar)_+_env(safe-area-inset-top,0px)_+_0.5rem)]",
        "md:inset-x-auto md:top-auto md:bottom-6 md:right-6 md:w-90",
      )}
    >
      {toasts.map((item) => (
        <Toast
          key={item.id}
          tone={item.tone ?? "neutral"}
          title={item.title}
          description={item.description}
          action={item.action}
          role={item.duration === 0 ? "alert" : "status"}
          onDismiss={() => dismiss(item.id)}
        />
      ))}
    </div>
  );
}
